import React, { FC, useCallback, useEffect } from 'react'
import { useStoreId } from '@moonpig/web-core-stores'
import {
  AddedToBasketEvent,
  useBasket,
  useHandleAddToBasket,
  PageTypeEnum,
  ProductTileProduct,
} from '@moonpig/web-shared-products'
import { getBrowserCookies } from '@moonpig/web-core-browser-storage'
import cookieValue from 'querystring'
import { useCustomerId } from '@moonpig/web-shared-utils'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { logger } from '@moonpig/web-core-monitoring'
import { Header as GalleryHeader } from '../Gallery'
import { SEARCH_TITLE_FOR_TRACKING } from '../../constants'
import { SearchResults } from '../SearchResults/SearchResults'
import { useFindLocaleText } from '../../text-localisation'
import {
  capitaliseFirstCharacter,
  useResetOffset,
  useSetUIHCookie,
} from '../../utils'
import { useSearchStore } from '../../store/SearchStore'
import { SearchHeader } from './SearchHeader'
import { DepartmentTabs } from '../DepartmentTabs/DepartmentTabs'
import { Box } from '@moonpig/launchpad-components'
import { usePageContextFacets } from '../../store/helpers'

const SearchTitle = SEARCH_TITLE_FOR_TRACKING

const capitaliseFirstLetters = (searchTerm: string): string => {
  return searchTerm.split(' ').map(capitaliseFirstCharacter).join(' ')
}

type SearchProps = {
  products: ProductTileProduct[]
  experiments?: { [key: string]: string } | undefined
  suggestions: ProductTileProduct[]
  loading: boolean
  customerId?: string
  metaTitle: string
  groupCardProject?: string
  pageType: PageTypeEnum
  totalClickRankDocumentCount: number
  totalCount: number
  department: DepartmentsEnum
}

export const Search: FC<SearchProps> = ({
  products,
  experiments,
  suggestions,
  metaTitle,
  groupCardProject,
  pageType,
  totalClickRankDocumentCount,
  totalCount,
  department,
}) => {
  useResetOffset(0)
  const storeId = useStoreId()
  const { setUIHCookie } = useSetUIHCookie()
  const searchContext = useSearchStore(store => store.searchContext)
  const noResultsFilters = useSearchStore(store => store.noResultsFilters)
  const integerFilters = useSearchStore(store => store.integerFilters)
  const localiseText = useFindLocaleText()
  const soldOutMessage = localiseText('find.sold_out')
  const itsNotYouMessage = localiseText('find.its_not_you')
  const noResultsDueToFiltersApplied = noResultsFilters.length > 0
  const isDynamic =
    pageType !== PageTypeEnum.SEARCH && pageType !== PageTypeEnum.COLLECTION

  const pageContextFacets = usePageContextFacets()

  const [_, { setLoading, setError }] = useBasket()

  const title = capitaliseFirstLetters(searchContext.searchTerm)

  const shouldDisplayZeroResults =
    (products &&
      products.length === 0 &&
      suggestions &&
      suggestions.length !== 0) ||
    noResultsDueToFiltersApplied

  const { responsiveSettings } = getBrowserCookies()
  const { EnableInternalUserMode: enableInternalUserMode = 'false' } =
    cookieValue.parse(responsiveSettings)
  const isInternalUser = /^true$/i.test(enableInternalUserMode as string)

  const customerId = useCustomerId()

  const { handleAddToBasket, addingToBasket } = useHandleAddToBasket({
    pageLocation: 'search',
    soldOutMessage,
    itsNotYouMessage,
    groupCardProject,
  })

  useEffect(() => {
    setLoading(addingToBasket)
  }, [addingToBasket, setLoading])

  const onAddedToBasket = useCallback(
    async (e: AddedToBasketEvent) => {
      const {
        product,
        variant,
        quantity,
        selectedAddon,
        buttonActionName,
        requiresEditingValue,
      } = e
      setUIHCookie({
        product,
        facets: pageContextFacets,
      })

      try {
        await handleAddToBasket({
          quantity,
          product,
          variant,
          filters: pageContextFacets,
          integerFilters,
          experiments,
          searchTerm: searchContext.searchTerm,
          selectedAddon,
          itemIndex: product?.productIndex || 0,
          productsLength:
            products.length || /* istanbul ignore next*/ suggestions.length,
          buttonActionName,
          requiresEditingValue,
        })
      } catch (error) {
        logger.fixToday('Failure adding product to basket', error)
        setError(true)
      }
    },
    [
      setUIHCookie,
      handleAddToBasket,
      experiments,
      searchContext.searchTerm,
      products.length,
      suggestions.length,
      setError,
      integerFilters,
      pageContextFacets,
    ],
  )

  const renderHeader = () => {
    const useAgeSelectionUx =
      experiments?.['search-use-age-selection-ux-v2']?.toLowerCase() ===
      'variant'

    if (pageType === PageTypeEnum.COLLECTION) {
      return (
        <Box mt={8}>
          <DepartmentTabs
            currentDepartment={department}
            searchContext={searchContext}
            withCounts
          />
        </Box>
      )
    }

    return isDynamic && totalCount > 0 ? (
      <GalleryHeader title={title} count={totalCount} />
    ) : (
      ((!noResultsDueToFiltersApplied && useAgeSelectionUx) ||
        !useAgeSelectionUx) && (
        <SearchHeader
          zeroResults={shouldDisplayZeroResults}
          searchContext={searchContext}
          department={department}
          totalCount={totalCount}
        />
      )
    )
  }

  const getBreadcrumb = () => {
    switch (searchContext.departments[0]) {
      case 'ALL_FLOWERS_AND_PLANTS':
        return {
          url: `/${storeId}/flowers_and_plants`,
          label: localiseText('common.flowers_and_plants'),
        }
      case 'ALL_GIFTS':
        return {
          url: `/${storeId}/gifts`,

          label: localiseText('common.gifts'),
        }
      default:
        return {
          url: `/${storeId}/personalised-cards`,
          label: localiseText('common.personalised_cards'),
        }
    }
  }

  const breadcrumbs = isDynamic
    ? [
        {
          url: `/${storeId}/`,
          label: localiseText('find.home'),
        },
        getBreadcrumb(),
      ]
    : []

  return (
    <SearchResults
      renderHeader={renderHeader}
      searchContext={searchContext}
      totalCount={totalCount}
      products={products}
      zeroResults={shouldDisplayZeroResults}
      experiments={experiments}
      suggestions={suggestions}
      breadcrumbs={breadcrumbs}
      isInternalUser={isInternalUser}
      addingToBasket={addingToBasket}
      onAddedToBasket={onAddedToBasket}
      searchTitle={isDynamic ? title : SearchTitle}
      pageType={isDynamic ? PageTypeEnum.DYNAMIC : pageType}
      customerId={customerId}
      metaTitle={metaTitle}
      totalClickRankDocumentCount={totalClickRankDocumentCount}
    />
  )
}
